import type { OperationRun } from '@foodlista/trigger-vue'
import type { ScopedOperationScope } from '../utils/scoped-operation-contract'
import {
  type MenuDetectMetadataInput,
  menuDetectMetadataManifest,
  type MenuDetectMetadataOutput,
  menuDetectMetadataOutputSchema,
  type MenuDetectMetadataProgressPhase,
} from '@foodlista/operations'
import { PRODUCTS_CACHE_KEYS } from '~/domains/products/queries'
import { RESTAURANTS_CACHE_KEYS } from '~/domains/restaurants/queries'
import { defineScopedOperation } from '../composables/defineScopedOperation'
import { presentMenuDetectMetadata } from '../presentation/presenters'
import { registerOperationPresenter } from '../presentation/registry'
import { registerOperationProgressTranslator } from '../progress/registry'
import { menuDetectMetadataScopePayload } from '../scope/payloads'

export type MenuMetadataInput = MenuDetectMetadataInput

export interface MenuMetadataScope extends ScopedOperationScope {
  flowId: string
}

export function parseMenuDetectMetadataOutput(
  run: OperationRun,
): MenuDetectMetadataOutput {
  return menuDetectMetadataOutputSchema.parse(run.output)
}

registerOperationProgressTranslator(
  menuDetectMetadataManifest.type,
  (phase, t) => {
    const typed = phase as MenuDetectMetadataProgressPhase
    switch (typed.kind) {
      case 'analyzing':
        return t('operations.boc.progress.menuDetectMetadata.analyzing')
      case 'detected':
        return t(
          'operations.boc.progress.menuDetectMetadata.detected',
          { count: typed.count },
          typed.count,
        )
      case 'fallback':
        return t('operations.boc.progress.menuDetectMetadata.fallback')
      case 'done':
        return t('operations.boc.progress.menuDetectMetadata.done')
    }
  },
)

registerOperationPresenter(
  menuDetectMetadataManifest.type,
  presentMenuDetectMetadata,
)

export const useMenuDetectMetadataOperation = defineScopedOperation<
  MenuMetadataInput,
  MenuMetadataScope,
  MenuDetectMetadataOutput
>({
  manifest: menuDetectMetadataManifest,
  idempotency: {
    mode: 'scope',
    keyFromScope: scope => scope.flowId,
  },
  scopePayload: menuDetectMetadataScopePayload,
  parseOutput: parseMenuDetectMetadataOutput,
  enqueue: (input, { orpc, buildEnqueueInput }) =>
    orpc.ai.detectMenuMetadataAsync(buildEnqueueInput(input)),
  // The run persists allergens + attributes server-side (by id). Once it lands
  // we refresh the caches so the (already-created) menu/products re-render with
  // their metadata — this survives modal close / page refresh.
  onCompleted: async ({ scope, queryClient }) => {
    if (!scope.restaurantSlug) {
      return
    }

    await Promise.all([
      queryClient.invalidateQueries({
        queryKey: RESTAURANTS_CACHE_KEYS.bySlug(scope.restaurantSlug),
      }),
      queryClient.invalidateQueries({
        queryKey: PRODUCTS_CACHE_KEYS.bySearch.all(scope.restaurantSlug),
      }),
    ])
  },
})
